import {
  footerSenderKind,
  hasLegalNoticeLink,
  MISSING_SENDER_MESSAGE,
  type SenderContext,
} from '@nopain-newsletter/core/sections';
import type { HtmlImportReport, TemplateKind } from '../../types/api';
import { MOCK_MEDIA_BASE, mockMediaPreviewSrc } from './rehost';

/**
 * Simplified stand-in for the server's HTML import (`packages/core`
 * `inspectHtmlImport`), so the import, edit and campaign-from-template
 * screens work with `VITE_MOCK=1`. It only covers the common Mailchimp
 * tags and checks (the footer sender rule is core's own, browser-safe
 * `footerSenderKind`); the real API is the reference.
 */

const MAX_BYTES = 100 * 1024;

const TAGS: Record<string, string> = {
  FNAME: '{{ contact.first_name | default: "there" }}',
  LNAME: '{{ contact.last_name }}',
  UNSUB: '{{ unsubscribe_url }}',
  ARCHIVE: '{{ view_url }}',
  'LIST:ADDRESS': '{{ company_address }}',
  'HTML:LIST_ADDRESS_HTML': '{{ company_address }}',
  UPDATE_PROFILE: '{{ preferences_url }}',
};

const UNSUPPORTED: Record<string, string> = {
  'MC:DATE': 'Write the date into the text instead.',
  CURRENT_YEAR: 'Write the year into the text instead.',
  EMAIL: 'The recipient email address is not available. Remove it.',
  FORWARD: 'Forward-to-a-friend does not exist. Remove the link.',
  'MC:SUBJECT': 'Write the subject line into the text instead.',
};

export function mockTemplateKind(blockJson: unknown): TemplateKind {
  return typeof blockJson === 'object' &&
    blockJson !== null &&
    Array.isArray((blockJson as { blocks?: unknown }).blocks)
    ? 'blocks'
    : 'html';
}

/** The known tag for `inner` (`FNAME:friend` becomes the first-name tag with that fallback), or null. */
function translateInner(inner: string): string | null {
  const direct = TAGS[inner];
  if (direct) return direct;
  const m = /^(FNAME|LNAME):([^"'\\{}<>|*]{1,60})$/.exec(inner);
  if (!m) return null;
  const field = m[1] === 'FNAME' ? 'contact.first_name' : 'contact.last_name';
  return `{{ ${field} | default: "${m[2]!.trim()}" }}`;
}

/** Translate the Mailchimp tags we know; unknown ones stay (and are reported). */
export function mockTranslateHtml(html: string): string {
  return html.replace(
    /\*\|([^|*{}<>"\\\r\n]{1,80}?)\|\*/g,
    (m, inner: string) => translateInner(inner) ?? m,
  );
}

function count(html: string, pattern: RegExp): number {
  return [...html.matchAll(pattern)].length;
}

export function mockHtmlCheck(input: string, sender: SenderContext = {}): HtmlImportReport {
  const sizeBytes = new TextEncoder().encode(input).length;
  const tooLarge = sizeBytes > MAX_BYTES;
  const translated = mockTranslateHtml(input);

  const problems: HtmlImportReport['problems'] = [];
  if (input.trim() === '') {
    problems.push({ code: 'empty', message: 'The template is empty.' });
  }
  if (tooLarge) {
    problems.push({
      code: 'too_large',
      message: `The HTML is larger than ${MAX_BYTES / 1024} KB. Host large images instead of embedding them.`,
    });
  }

  const translations: HtmlImportReport['translations'] = [];
  for (const m of input.matchAll(/\*\|([^|*{}<>"\\\r\n]{1,80}?)\|\*/g)) {
    const inner = m[1]!;
    const to = translateInner(inner);
    if (to) {
      const existing = translations.find((t) => t.tag === m[0]);
      if (existing) existing.count += 1;
      else translations.push({ tag: m[0], to, count: 1 });
    } else {
      const advice = UNSUPPORTED[inner] ?? 'This merge tag is not supported. Remove it.';
      const existing = problems.find((p) => p.tag === m[0]);
      if (existing) existing.count = (existing.count ?? 1) + 1;
      else problems.push({ code: 'mailchimp_merge_tag', tag: m[0], message: advice, count: 1 });
    }
  }

  const imgs = [...translated.matchAll(/<img\b[^>]*>/gi)].map((m) => {
    const tag = m[0];
    const src = /\ssrc\s*=\s*["']([^"']*)["']/i.exec(tag)?.[1] ?? '';
    const alt = /\salt\s*=\s*["']([^"']*)["']/i.exec(tag)?.[1] ?? null;
    let host: string | null = null;
    let kind: 'hosted' | 'base64' | 'relative' | 'merge' = 'relative';
    if (src.startsWith('data:')) kind = 'base64';
    else if (/^https?:\/\//i.test(src)) {
      kind = 'hosted';
      host = /^https?:\/\/([^/?#]+)/i.exec(src)?.[1]?.toLowerCase() ?? null;
    }
    return { src: kind === 'base64' ? 'data:image' : src, alt, kind, host };
  });
  const mailchimp = (h: string | null) =>
    h !== null && /mcusercontent\.com|list-manage\.com|chimpstatic\.com/.test(h);
  const hostCounts = new Map<string, number>();
  for (const i of imgs) if (i.host) hostCounts.set(i.host, (hostCounts.get(i.host) ?? 0) + 1);
  if (imgs.some((i) => i.kind === 'base64')) {
    problems.push({
      code: 'base64_image',
      message: 'Images embedded in the HTML are not allowed. Host the images and link to them.',
    });
  }
  if (imgs.some((i) => i.kind === 'relative')) {
    problems.push({
      code: 'relative_image_src',
      message: 'An image has no full web address. Use a full https address.',
    });
  }

  const hasUnsub = /\{\{\s*unsubscribe_url\s*\}\}/.test(translated);
  const senderKind = footerSenderKind(translated, sender);
  if (input.trim() !== '' && !hasUnsub) {
    problems.push({
      code: 'missing_unsubscribe',
      message: 'The footer needs an unsubscribe link ({{ unsubscribe_url }}).',
    });
  }
  if (input.trim() !== '' && senderKind === 'missing') {
    problems.push({ code: 'missing_sender_details', message: MISSING_SENDER_MESSAGE });
  }

  const removed: HtmlImportReport['removed'] = [];
  const scripts = count(input, /<script\b/gi);
  if (scripts > 0) removed.push({ kind: 'tag', name: 'script', count: scripts });
  const comments = count(input, /<!--/g);
  if (comments > 0) {
    removed.push({
      kind: 'comment',
      name: 'conditional',
      count: comments,
      note: 'Outlook-only (conditional) comments are removed. Outlook will show the normal version of those parts.',
    });
  }

  const previewHtml = tooLarge
    ? null
    : input.trim() === ''
      ? null
      : translated
          .replace(/<script\b[\s\S]*?<\/script>/gi, '')
          .replace(/<!--[\s\S]*?-->/g, '')
          .replace(/\{\{\s*contact\.first_name[^}]*\}\}/g, 'Anna')
          .replace(/\{\{\s*contact\.last_name\s*\}\}/g, 'Jensen')
          .replace(
            /\{\{\s*(unsubscribe_url|view_url|preferences_url)\s*\}\}/g,
            'https://eksempel.dk/preview',
          )
          .replace(/\{\{\s*company_address\s*\}\}/g, sender.senderDetails ?? 'NoPain® Cream')
          .replace(
            /\{\{\s*legal_notice_url\s*\}\}/g,
            sender.legalNoticeUrl ?? 'https://eksempel.dk/preview',
          )
          // The pretend media domain does not exist: show the copy held in memory instead.
          .replace(
            new RegExp(
              `${MOCK_MEDIA_BASE.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')}/[0-9a-z.]+`,
              'g',
            ),
            (url) => mockMediaPreviewSrc(url) ?? url,
          );

  return {
    html: tooLarge ? '' : translated,
    valid: problems.length === 0,
    problems,
    translations,
    removed,
    images: {
      total: imgs.length,
      hosted: imgs.filter((i) => i.kind === 'hosted').length,
      base64: imgs.filter((i) => i.kind === 'base64').length,
      relative: imgs.filter((i) => i.kind === 'relative').length,
      mailchimpHosted: imgs.filter((i) => mailchimp(i.host)).length,
      ownHosted: imgs.filter((i) => i.kind === 'hosted' && i.src.startsWith(`${MOCK_MEDIA_BASE}/`))
        .length,
      hosts: [...hostCounts].map(([host, n]) => ({ host, count: n, mailchimp: mailchimp(host) })),
      items: imgs.slice(0, 200),
    },
    footer: {
      unsubscribeLink: hasUnsub,
      sender: senderKind,
      legalNotice: hasLegalNoticeLink(translated, sender),
    },
    sizeBytes,
    maxBytes: MAX_BYTES,
    previewHtml,
  };
}

/** A Mailchimp-style table newsletter for the sample data (already translated, as the server stores it). */
export const MOCK_HTML_TEMPLATE = `<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4efe9;font-family:Georgia,serif;">
<tr><td align="center" style="padding:24px 12px;">
<table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;max-width:600px;">
<tr><td><img src="https://mcusercontent.com/nopain/images/hero-sept.jpg" alt="September offer" width="600" style="display:block;width:100%;background:#d9c9b8;height:180px;"></td></tr>
<tr><td style="padding:28px 32px 8px;font-size:26px;line-height:32px;color:#2b2118;">Hi {{ contact.first_name | default: "there" }}, the tattoo bundle is back</td></tr>
<tr><td style="padding:0 32px 20px;font-size:16px;line-height:24px;color:#4a3f35;">Our numbing cream and aftercare balm in one box. This text is sample data.</td></tr>
<tr><td align="center" style="padding:0 32px 28px;"><a href="https://eksempel.dk/bundle" style="display:inline-block;background:#2b2118;color:#ffffff;padding:14px 28px;text-decoration:none;font-size:16px;">Get the bundle</a></td></tr>
<tr><td style="padding:20px 32px;background:#f4efe9;font-size:12px;line-height:18px;color:#7a6c5f;text-align:center;">{{ company_address }}<br><a href="{{ unsubscribe_url }}" style="color:#7a6c5f;">Unsubscribe</a> &middot; <a href="{{ preferences_url }}" style="color:#7a6c5f;">Update your preferences</a></td></tr>
</table>
</td></tr>
</table>`;
